import { useQueries } from '@tanstack/react-query';

import { findBrandKey, findModelKey } from '../domain/carKeys';
import type { EngineSpec } from '../domain/expenses';
import type { GenerationVariationKey } from '../domain/pricing';
import * as adMap from '../mappings/ad_mappings';
import { fetchStorageJson } from '../services/firebaseStorage';

const DEFAULT_EMISSION_STANDARD = 'EURO IV';

function normalizeEngineSpec(raw: EngineSpec): EngineSpec {
  const spec = { ...raw };

  if (!spec.emissionStandard) {
    spec.emissionStandard = DEFAULT_EMISSION_STANDARD;
  }

  return spec;
}

function toKey(generation: string, variation: string): string {
  return `${generation}/${variation}`;
}

/** Engine/spec details for each generation+variation, fetched from `data/<brand-slug>/<model-slug>/...`. */
export function useEngineSpecs(make: string, model: string, generationVariationKeys: GenerationVariationKey[]) {
  const brandKey = findBrandKey(make);
  const modelKey = brandKey ? findModelKey(brandKey, model) : null;
  const brandSlug = brandKey ? adMap.DATA_BRANDS[brandKey] : undefined;
  const modelSlug = brandKey && modelKey ? adMap.DATA_MODELS[brandKey]?.[modelKey] : undefined;

  const queries = useQueries({
    queries: generationVariationKeys.map(({ generation, variation }) => ({
      queryKey: ['engineSpec', brandSlug, modelSlug, generation, variation],
      queryFn: () =>
        fetchStorageJson<EngineSpec>(`data/${brandSlug}/${modelSlug}/${generation}/${variation}/parsed.json`).then(
          normalizeEngineSpec,
        ),
      enabled: Boolean(brandSlug && modelSlug),
      staleTime: Infinity,
    })),
  });

  const specsByKey = new Map<string, EngineSpec>();
  queries.forEach((query, index) => {
    if (query.data) {
      const { generation, variation } = generationVariationKeys[index];
      specsByKey.set(toKey(generation, variation), query.data);
    }
  });

  return {
    specsByKey,
    isLoading: generationVariationKeys.length > 0 && queries.some((query) => query.isLoading),
  };
}
